import { useState, useEffect, useMemo } from "react";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  AreaChart,
  Area,
} from "recharts";
import {
  TrendingUp,
  TrendingDown,
  Package,
  Warehouse,
  DollarSign,
  BarChart3,
  PieChartIcon,
  Activity,
  Calendar,
  Download,
} from "lucide-react";
import { format, parseISO, subDays, startOfDay, isAfter } from "date-fns";
import { dashboardApi, productApi, transactionApi, warehouseApi, exportApi } from "../lib/api";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";

const COLORS = {
  primary: "#3B82F6",
  accent: "#EF4444",
  success: "#10B981",
  warning: "#F59E0B",
  purple: "#8B5CF6",
  cyan: "#06B6D4",
};

const PIE_COLORS = [COLORS.primary, COLORS.accent, COLORS.success, COLORS.warning, COLORS.purple, COLORS.cyan];

// Legend formatter function (defined outside component to avoid re-creation on each render)
const legendFormatter = (value) => <span className="text-slate-300 text-xs">{value}</span>;

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-[#151923] border border-slate-700 rounded-sm p-3 shadow-xl">
        <p className="text-slate-400 text-xs mb-2">{label}</p>
        {payload.map((entry, index) => (
          <div key={index} className="flex items-center gap-2">
            <div
              className="w-2 h-2 rounded-full"
              style={{ backgroundColor: entry.color }}
            />
            <span className="text-sm text-slate-300">{entry.name}:</span>
            <span className="text-sm font-mono text-white">
              {typeof entry.value === "number" && entry.name.includes("Value")
                ? `$${entry.value.toLocaleString()}`
                : entry.value.toLocaleString()}
            </span>
          </div>
        ))}
      </div>
    );
  }
  return null;
};

const StatCard = ({ title, value, icon: Icon, change, changeType, color = "blue" }) => {
  const colorClasses = {
    blue: "text-blue-400 bg-blue-500/10",
    green: "text-emerald-400 bg-emerald-500/10",
    red: "text-red-400 bg-red-500/10",
    yellow: "text-amber-400 bg-amber-500/10",
  };

  return (
    <div className="bg-[#151923] border border-slate-800 rounded-sm p-5 card-hover">
      <div className="flex items-start justify-between mb-3">
        <div className={`p-2 rounded-sm ${colorClasses[color]}`}>
          <Icon className="w-5 h-5" />
        </div>
        {change !== undefined && (
          <div
            className={`flex items-center gap-1 text-xs ${
              changeType === "positive" ? "text-emerald-400" : "text-red-400"
            }`}
          >
            {changeType === "positive" ? (
              <TrendingUp className="w-3 h-3" />
            ) : (
              <TrendingDown className="w-3 h-3" />
            )}
            {change}%
          </div>
        )}
      </div>
      <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">{title}</p>
      <p className="text-xl font-bold text-white font-mono">{value}</p>
    </div>
  );
};

const ChartCard = ({ title, icon: Icon, children }) => (
  <div className="bg-[#151923] border border-slate-800 rounded-sm p-5">
    <div className="flex items-center gap-2 mb-4">
      <Icon className="w-5 h-5 text-blue-400" />
      <h3 className="text-base font-semibold text-white">{title}</h3>
    </div>
    {children}
  </div>
);

export default function Reports() {
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState("7");
  const [products, setProducts] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [dashboardStats, setDashboardStats] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [productsRes, transactionsRes, warehousesRes, statsRes] = await Promise.all([
        productApi.getAll(),
        transactionApi.getAll({ limit: 1000 }),
        warehouseApi.getAll(),
        dashboardApi.getStats(),
      ]);
      setProducts(productsRes.data);
      setTransactions(transactionsRes.data);
      setWarehouses(warehousesRes.data);
      setDashboardStats(statsRes.data);
    } catch (error) {
      toast.error("Failed to load report data");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Transaction trends by date
  const transactionTrends = useMemo(() => {
    const days = parseInt(timeRange);
    const cutoffDate = startOfDay(subDays(new Date(), days));
    
    const filteredTx = transactions.filter((tx) => {
      try {
        const txDate = parseISO(tx.created_at);
        return isAfter(txDate, cutoffDate);
      } catch {
        return false;
      }
    });

    const grouped = {};
    filteredTx.forEach((tx) => {
      try {
        const date = format(parseISO(tx.created_at), "MMM dd");
        if (!grouped[date]) {
          grouped[date] = { date, inQty: 0, outQty: 0, inValue: 0, outValue: 0 };
        }
        if (tx.transaction_type === "in") {
          grouped[date].inQty += tx.quantity;
          grouped[date].inValue += tx.total;
        } else {
          grouped[date].outQty += tx.quantity;
          grouped[date].outValue += tx.total;
        }
      } catch {
        // Skip invalid date entries
      }
    });

    return Object.values(grouped).sort((a, b) => {
      const dateA = new Date(a.date + " 2024");
      const dateB = new Date(b.date + " 2024");
      return dateA - dateB;
    });
  }, [transactions, timeRange]);

  // Inventory value by warehouse
  const warehouseDistribution = useMemo(() => {
    const warehouseMap = {};
    warehouses.forEach((w) => {
      warehouseMap[w.id] = { name: w.name, value: 0, items: 0 };
    });

    products.forEach((p) => {
      if (warehouseMap[p.warehouse_id]) {
        warehouseMap[p.warehouse_id].value += p.quantity * p.unit_price;
        warehouseMap[p.warehouse_id].items += p.quantity;
      }
    });

    return Object.values(warehouseMap)
      .filter((w) => w.value > 0)
      .map((w) => ({ ...w, value: Math.round(w.value * 100) / 100 }));
  }, [products, warehouses]);

  // Top products by value
  const topProducts = useMemo(() => {
    return [...products]
      .map((p) => ({
        name: p.name.length > 15 ? p.name.substring(0, 15) + "..." : p.name,
        fullName: p.name,
        sku: p.sku,
        value: p.quantity * p.unit_price,
        quantity: p.quantity,
      }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 8);
  }, [products]);

  // Stock level distribution
  const stockLevelDistribution = useMemo(() => {
    let critical = 0;
    let low = 0;
    let normal = 0;
    let high = 0;

    products.forEach((p) => {
      if (p.quantity === 0) critical++;
      else if (p.quantity <= p.min_stock) low++;
      else if (p.quantity <= p.min_stock * 2) normal++;
      else high++;
    });

    return [
      { name: "Out of Stock", value: critical, color: COLORS.accent },
      { name: "Low Stock", value: low, color: COLORS.warning },
      { name: "Normal", value: normal, color: COLORS.primary },
      { name: "Well Stocked", value: high, color: COLORS.success },
    ].filter((item) => item.value > 0);
  }, [products]);

  // Summary stats
  const summaryStats = useMemo(() => {
    const totalValue = products.reduce((acc, p) => acc + p.quantity * p.unit_price, 0);
    const totalItems = products.reduce((acc, p) => acc + p.quantity, 0);
    const avgValue = products.length > 0 ? totalValue / products.length : 0;
    
    const recentInValue = transactions
      .filter((t) => t.transaction_type === "in")
      .reduce((acc, t) => acc + t.total, 0);
    const recentOutValue = transactions
      .filter((t) => t.transaction_type === "out")
      .reduce((acc, t) => acc + t.total, 0);

    return {
      totalValue: Math.round(totalValue * 100) / 100,
      totalItems,
      avgValue: Math.round(avgValue * 100) / 100,
      recentInValue: Math.round(recentInValue * 100) / 100,
      recentOutValue: Math.round(recentOutValue * 100) / 100,
      netFlow: Math.round((recentInValue - recentOutValue) * 100) / 100,
    };
  }, [products, transactions]);

  const handleExport = (type) => {
    const url = type === "products" ? exportApi.products() : exportApi.transactions();
    window.open(url, "_blank");
    toast.success(`Exporting ${type} data...`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="animate-fadeIn" data-testid="reports-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-bold text-white mb-1">Reports</h1>
          <p className="text-slate-400">Analytics and inventory insights</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={timeRange} onValueChange={setTimeRange}>
            <SelectTrigger className="w-[140px] bg-[#0B0E14] border-slate-700">
              <Calendar className="w-4 h-4 mr-2 text-slate-400" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="14">Last 14 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleExport("products")}
            className="bg-[#0B0E14] border-slate-700 text-slate-300 hover:bg-slate-800"
          >
            <Download className="w-4 h-4 mr-2" />
            Export
          </Button>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
        <StatCard
          title="Total Value"
          value={`$${summaryStats.totalValue.toLocaleString()}`}
          icon={DollarSign}
          color="green"
        />
        <StatCard
          title="Total Items"
          value={summaryStats.totalItems.toLocaleString()}
          icon={Package}
          color="blue"
        />
        <StatCard
          title="Warehouses"
          value={warehouses.length}
          icon={Warehouse}
          color="yellow"
        />
        <StatCard
          title="Total In"
          value={`$${summaryStats.recentInValue.toLocaleString()}`}
          icon={TrendingUp}
          color="green"
        />
        <StatCard
          title="Total Out"
          value={`$${summaryStats.recentOutValue.toLocaleString()}`}
          icon={TrendingDown}
          color="red"
        />
        <StatCard
          title="Net Flow"
          value={`$${summaryStats.netFlow.toLocaleString()}`}
          icon={Activity}
          color={summaryStats.netFlow >= 0 ? "green" : "red"}
          change={summaryStats.netFlow >= 0 ? undefined : undefined}
          changeType={summaryStats.netFlow >= 0 ? "positive" : "negative"}
        />
      </div>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Transaction Trends */}
        <ChartCard title="Inventory Flow Trends" icon={Activity}>
          {transactionTrends.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={transactionTrends}>
                <defs>
                  <linearGradient id="inGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={COLORS.success} stopOpacity={0.3} />
                    <stop offset="95%" stopColor={COLORS.success} stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="outGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={COLORS.accent} stopOpacity={0.3} />
                    <stop offset="95%" stopColor={COLORS.accent} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis dataKey="date" tick={{ fill: "#94A3B8", fontSize: 11 }} />
                <YAxis tick={{ fill: "#94A3B8", fontSize: 11 }} />
                <Tooltip content={<CustomTooltip />} />
                <Legend
                  wrapperStyle={{ paddingTop: "10px" }}
                  formatter={legendFormatter}
                />
                <Area
                  type="monotone"
                  dataKey="inValue"
                  name="In Value"
                  stroke={COLORS.success}
                  fill="url(#inGradient)"
                  strokeWidth={2}
                />
                <Area
                  type="monotone"
                  dataKey="outValue"
                  name="Out Value"
                  stroke={COLORS.accent}
                  fill="url(#outGradient)"
                  strokeWidth={2}
                />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[280px] flex items-center justify-center text-slate-500">
              No transaction data for this period
            </div>
          )}
        </ChartCard>

        {/* Top Products */}
        <ChartCard title="Top Products by Value" icon={BarChart3}>
          {topProducts.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={topProducts} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" horizontal={false} />
                <XAxis type="number" tick={{ fill: "#94A3B8", fontSize: 11 }} />
                <YAxis
                  type="category"
                  dataKey="name"
                  tick={{ fill: "#94A3B8", fontSize: 11 }}
                  width={100}
                />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="value" name="Value" fill={COLORS.primary} radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[280px] flex items-center justify-center text-slate-500">
              No product data available
            </div>
          )}
        </ChartCard>
      </div>

      {/* Charts Row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        {/* Warehouse Distribution */}
        <ChartCard title="Value by Warehouse" icon={Warehouse}>
          {warehouseDistribution.length > 0 ? (
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie
                  data={warehouseDistribution}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={80}
                  paddingAngle={2}
                  dataKey="value"
                  label={({ name, percent }) =>
                    `${name} (${(percent * 100).toFixed(0)}%)`
                  }
                  labelLine={{ stroke: "#64748B" }}
                >
                  {warehouseDistribution.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={PIE_COLORS[index % PIE_COLORS.length]}
                    />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[240px] flex items-center justify-center text-slate-500">
              No warehouse data available
            </div>
          )}
        </ChartCard>

        {/* Stock Level Distribution */}
        <ChartCard title="Stock Level Distribution" icon={PieChartIcon}>
          {stockLevelDistribution.length > 0 ? (
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie
                  data={stockLevelDistribution}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={80}
                  paddingAngle={2}
                  dataKey="value"
                  label={({ name, value }) => `${name}: ${value}`}
                  labelLine={{ stroke: "#64748B" }}
                >
                  {stockLevelDistribution.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[240px] flex items-center justify-center text-slate-500">
              No stock data available
            </div>
          )}
        </ChartCard>

        {/* Quantity Flow */}
        <ChartCard title="Quantity Movement" icon={Activity}>
          {transactionTrends.length > 0 ? (
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={transactionTrends}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis dataKey="date" tick={{ fill: "#94A3B8", fontSize: 10 }} />
                <YAxis tick={{ fill: "#94A3B8", fontSize: 10 }} />
                <Tooltip content={<CustomTooltip />} />
                <Legend
                  formatter={legendFormatter}
                />
                <Line
                  type="monotone"
                  dataKey="inQty"
                  name="In Qty"
                  stroke={COLORS.success}
                  strokeWidth={2}
                  dot={{ fill: COLORS.success, r: 3 }}
                />
                <Line
                  type="monotone"
                  dataKey="outQty"
                  name="Out Qty"
                  stroke={COLORS.accent}
                  strokeWidth={2}
                  dot={{ fill: COLORS.accent, r: 3 }}
                />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[240px] flex items-center justify-center text-slate-500">
              No transaction data for this period
            </div>
          )}
        </ChartCard>
      </div>

      {/* Export Options */}
      <div className="bg-[#151923] border border-slate-800 rounded-sm p-5">
        <h3 className="text-base font-semibold text-white mb-4">Export Data</h3>
        <div className="flex flex-wrap gap-3">
          <Button
            onClick={() => handleExport("products")}
            className="bg-blue-700 hover:bg-blue-600"
          >
            <Download className="w-4 h-4 mr-2" />
            Export Products CSV
          </Button>
          <Button
            onClick={() => handleExport("transactions")}
            variant="outline"
            className="bg-[#0B0E14] border-slate-700 text-slate-300 hover:bg-slate-800"
          >
            <Download className="w-4 h-4 mr-2" />
            Export Transactions CSV
          </Button>
        </div>
      </div>
    </div>
  );
}
